import React, { useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { CloudUpload, DeleteForeverRounded } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../api/snackbarSlice';

const MAX_FILE_SIZE = 500 * 1024;

const LogoUpload = ({ initialLogoUrl, onFileChange, readOnly = false }) => {
  const inputRef = useRef();
  const dispatch = useDispatch();
  const [previewUrl, setPreviewUrl] = useState(initialLogoUrl || null);
  const [file, setFile] = useState(null);

  const handleUploadLogo = (uploadedFile) => {
    if (readOnly || !uploadedFile) return;

    if (uploadedFile.size > MAX_FILE_SIZE) {
      dispatch(showSnackbar({ message: 'File exceeds max size (500KB).', severity: 'warning' }));
      return;
    }

    if (
      !uploadedFile.name.endsWith('.png') &&
      !uploadedFile.name.endsWith('.jpeg') &&
      !uploadedFile.name.endsWith('.jpg')
    ) {
      dispatch(
        showSnackbar({
          message: 'File format not supported. Please upload .jpeg, .png or .jpg files only.',
          severity: 'warning',
        }),
      );
      return;
    }

    setFile(uploadedFile);
    setPreviewUrl(URL.createObjectURL(uploadedFile));
    onFileChange(uploadedFile);
  };

  const handleClearFile = () => {
    setPreviewUrl(null);
    setFile(null);
    onFileChange(null);
  };

  const onDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    handleUploadLogo(droppedFile);
  };

  if (!previewUrl) {
    return (
      <Box
        onClick={() => {
          if (!readOnly) inputRef.current?.click();
        }}
        onDragOver={(e) => {
          if (!readOnly) e.preventDefault();
        }}
        onDrop={(e) => {
          if (!readOnly) onDrop(e);
        }}
        sx={{
          border: '2px dashed #ccc',
          borderRadius: '8px',
          textAlign: 'center',
          backgroundColor: '#f0f0f0',
          cursor: readOnly ? 'not-allowed' : 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
        }}
      >
        <input
          type="file"
          accept=".png,.jpeg,.jpg"
          ref={inputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleUploadLogo(e.target.files?.[0])}
        />
        <CloudUpload fontSize="large" color="action" sx={{ mt: 7 }} />
        <Typography variant="body2" sx={{ mt: 7 }}>
          {readOnly ? 'No logo available' : 'Drag & Drop logo here'}
        </Typography>
        {!readOnly && (
          <Typography variant="caption" color="textSecondary">
            Allowed file types: JPG, JPEG, PNG · Max size: 500KB
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        borderRadius: '8px',
        position: 'relative',
      }}
    >
      <img src={previewUrl} alt="Preview" style={{ maxWidth: 350, maxHeight: 200, borderRadius: 8 }} />
      {!readOnly && (
        <IconButton onClick={handleClearFile}>
          <DeleteForeverRounded color="error" />
        </IconButton>
      )}
    </Box>
  );
};

export default LogoUpload;
